// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AppIcon, type SvgIconName } from '@shared/ui-kit';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { type FC, type ReactNode } from 'react';
import { downloadDocumentFile } from '../../utils/download-document-files';
import { formatDocumentFileSize } from '../../utils/format-document-file-size';
import {
  formatFileTypeLabel,
  getDocumentFileIcon,
  splitFileNameForDisplay,
} from '../../utils/get-document-file-display';

export interface DocumentFileListItemProps {
  readonly fileName: string;
  readonly fileSize: number;
  readonly downloadUrl?: string;
  readonly downloadAriaLabel?: string;
  readonly downloadTestId?: string;
  readonly action?: ReactNode;
  readonly testId?: string;
  readonly surface?: 'page' | 'section';
  readonly title?: string;
  readonly metaLabel?: string;
  readonly displayIcon?: SvgIconName;
}

export const DocumentFileListItem: FC<DocumentFileListItemProps> = ({
  fileName,
  fileSize,
  downloadUrl,
  downloadAriaLabel,
  downloadTestId,
  action,
  testId,
  surface = 'page',
  title,
  metaLabel,
  displayIcon,
}) => {
  const resolvedTitle = title ?? fileName;
  const nameParts = title
    ? { baseName: title, extensionSuffix: '' }
    : splitFileNameForDisplay(fileName);
  const fileTypeLabel = formatFileTypeLabel(fileName);
  const fileIcon = displayIcon ?? getDocumentFileIcon(fileName);
  const iconWrapClassName = surface === 'page' ? 'bg-section' : 'bg-page';
  const secondaryLine =
    metaLabel ?? `${fileTypeLabel} · ${formatDocumentFileSize(fileSize)}`;

  return (
    <li
      className={classNames(
        'flex items-center gap-3 rounded-lg px-3 py-3',
        surface === 'page' ? 'bg-page' : 'bg-section',
      )}
      data-testid={testId}
    >
      <span
        className={classNames(
          'flex size-11 shrink-0 items-center justify-center rounded-lg',
          iconWrapClassName,
        )}
      >
        <AppIcon icon={fileIcon} size="lg" color="main" />
      </span>

      <div className="min-w-0 flex-1">
        <p
          className="flex min-w-0 items-baseline text-base font-medium text-title-dark"
          title={resolvedTitle}
        >
          <span className="truncate">{nameParts.baseName}</span>
          {nameParts.extensionSuffix ? (
            <span className="shrink-0">{nameParts.extensionSuffix}</span>
          ) : null}
        </p>
        <p className="text-sm text-primary-medium">{secondaryLine}</p>
      </div>

      {(downloadUrl && downloadAriaLabel) || action ? (
        <div className="flex shrink-0 items-center gap-2">
          {downloadUrl && downloadAriaLabel ? (
            <IconButton
              icon="Download"
              variant="icon"
              size="lg"
              color="main"
              aria-label={downloadAriaLabel}
              data-testid={downloadTestId}
              onClick={() => downloadDocumentFile(downloadUrl, fileName)}
            />
          ) : null}
          {action}
        </div>
      ) : null}
    </li>
  );
};
